/**
 * Financeiro v2 (fase 6): partes desligáveis, situação de vencimento e
 * formatação comum às telas. A regra de vencido é do servidor (campo
 * `isOverdue` e filtros por situação); aqui só se exibe.
 */
export const FINANCE_FEATURES = ['btg', 'boleto', 'invoices', 'transfers', 'carne'] as const
export type FinanceFeature = (typeof FINANCE_FEATURES)[number]
export type FinanceFeatureMap = Record<FinanceFeature, boolean>

/** Enquanto não sabemos (ou se a consulta falhar), tudo desligado. */
export const ALL_FEATURES_OFF: FinanceFeatureMap = { btg: false, boleto: false, invoices: false, transfers: false, carne: false }

export const FEATURE_LABELS: Record<FinanceFeature, string> = {
  btg: 'Integração BTG',
  boleto: 'Boleto',
  invoices: 'Faturas e registro de horas',
  transfers: 'Transferências',
  carne: 'Carnê',
}

/** Parte de cada tela do financeiro (null = sempre ligada). */
const ROUTE_FEATURES: Array<[RegExp, FinanceFeature]> = [
  [/^\/finance-v2\/btg-config/, 'btg'],
  [/^\/finance-v2\/carne/, 'carne'],
  [/^\/finance-v2\/transfers/, 'transfers'],
  [/^\/finance-v2\/(invoices|service-types|timesheets)/, 'invoices'],
]

export function featureForPath(path: string): FinanceFeature | null {
  return ROUTE_FEATURES.find(([pattern]) => pattern.test(path))?.[1] ?? null
}

/** Situação de vencimento: o que o filtro "Situação" da listagem aceita. */
export const STATUS_FILTERS = [
  { value: 'OPEN', label: 'Em aberto' },
  { value: 'PENDING', label: 'A vencer' },
  { value: 'OVERDUE', label: 'Vencido' },
  { value: 'PAID', label: 'Pago' },
  { value: 'CANCELLED', label: 'Cancelado' },
] as const
export type StatusFilter = (typeof STATUS_FILTERS)[number]['value']

export function situationLabel(t: { status: string; isOverdue?: boolean }): string {
  if (t.status === 'PAID') return 'Pago'
  if (t.status === 'CANCELLED') return 'Cancelado'
  return t.isOverdue || t.status === 'OVERDUE' ? 'Vencido' : 'A vencer'
}

export function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, evita desvio de fuso). */
export function formatIsoDate(iso: string | null | undefined): string {
  const day = String(iso ?? '').slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day.split('-').reverse().join('/') : '-'
}

export function dueInLabel(days: number): string {
  if (days < 0) return `vencido há ${-days} dia${days === -1 ? '' : 's'}`
  if (days === 0) return 'vence hoje'
  return `vence em ${days} dia${days === 1 ? '' : 's'}`
}
